import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getAzpneuDezen, schvalitAzpneu, skrytAzpneu, nastavCenuAzpneu,
  type AzpneuDezenDetail,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { AlertIcon, PackageIcon } from "../lib/icons.js";

/**
 * Detail DEZÉNU — tedy toho, co na eshopu vznikne jako jeden produkt.
 * Rozměry jsou jeho varianty a schvalují se po jednom, protože je dodavatel
 * má skladem každý zvlášť.
 *
 * 💰 Rozpis „z čeho ta cena vyšla" je tu schválně: bez něj se nedá poznat,
 * jestli je cena vysoká kvůli nákupce, dopravě, nebo marži.
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

export function AzpneuDetailPage(): JSX.Element {
  const { brand = "", pattern = "" } = useParams();
  const [d, setD] = useState<AzpneuDezenDetail | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [vybrany, setVybrany] = useState<string | null>(null);

  const nacti = useCallback(async () => {
    try { setD(await getAzpneuDezen(brand, pattern)); }
    catch (e) { setChyba(e instanceof Error ? e.message : "nepovedlo se"); }
  }, [brand, pattern]);

  useEffect(() => { void nacti(); }, [nacti]);

  if (chyba) return <EmptyState icon={<PackageIcon size={28} />} title="Nenalezeno" description={chyba} />;
  if (!d) return <LoadingBlock label="Načítám…" />;

  const r = d.rozmery.find((x) => x.azpneuId === vybrany) ?? d.rozmery[0];

  return (
    <div className="p-6 max-w-6xl">
      <nav className="mb-4 text-sm text-zinc-500">
        <Link to="/azpneu" className="hover:text-ktm-orange">AZ PNEU</Link>
        <span className="mx-2 text-zinc-300">›</span>
        <span>{d.brand}</span>
        {d.kategorie && (<><span className="mx-2 text-zinc-300">›</span><span>{d.kategorie}</span></>)}
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 aspect-square grid place-items-center overflow-hidden">
            {d.imgUrl
              ? <img src={d.imgUrl} alt={d.pattern} className="w-full h-full object-contain p-8" />
              : <span className="text-zinc-300">bez fotky</span>}
          </div>
          <p className="mt-2 text-[11px] text-zinc-400">
            ⚠ Fotka je od dodavatele a je společná pro celý dezén — u pneumatik
            se nefotí každý rozměr zvlášť.
          </p>
        </div>

        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {d.naEshopu > 0
              ? <Badge tone="green">{d.naEshopu} z {d.rozmery.length} na eshopu</Badge>
              : <Badge tone="amber">Nevedeme</Badge>}
            {d.kategorie && <Badge tone="neutral">{d.kategorie}</Badge>}
            {d.construction && <Badge tone="neutral">{d.construction}</Badge>}
          </div>

          <h1 className="text-2xl font-semibold">{d.brand} {d.pattern}</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {d.rozmery.length} rozměrů · dodavatel az-pneu
          </p>

          {/* 💰 Rozpis ceny pro vybraný rozměr */}
          {r && (
            <div className="mt-5 rounded-xl border border-zinc-200 p-4">
              <div className="text-xs text-zinc-500 mb-2">
                Z čeho vyšla cena — rozměr <strong>{r.sizeLabel}</strong>
                {r.position && ` (${r.position === "P" ? "přední" : "zadní"})`}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  <tr><td className="py-1 text-zinc-600">nákup u dodavatele (bez DPH)</td>
                      <td className="py-1 text-right tabular-nums">{kc(r.nakupka)}</td></tr>
                  <tr><td className="py-1 text-zinc-600">+ doprava (za kus, ne za zásilku)</td>
                      <td className="py-1 text-right tabular-nums">{kc(d.vzorec.dopravaKs)}</td></tr>
                  <tr className="border-t border-zinc-100">
                      <td className="py-1 font-medium">= nákladovka</td>
                      <td className="py-1 text-right tabular-nums font-medium">
                        {kc(Number(r.nakupka ?? 0) + d.vzorec.dopravaKs)}</td></tr>
                  <tr><td className="py-1 text-zinc-600">+ marže min {Math.round(d.vzorec.marzeMin * 100)} %</td>
                      <td className="py-1 text-right tabular-nums">
                        {kc((Number(r.nakupka ?? 0) + d.vzorec.dopravaKs) * d.vzorec.marzeMin)}</td></tr>
                  <tr><td className="py-1 text-zinc-600">+ DPH 21 %</td><td /></tr>
                  <tr className="border-t border-zinc-200">
                      <td className="py-2 font-semibold">= naše cena</td>
                      <td className="py-2 text-right tabular-nums text-lg font-semibold">
                        {kc(r.naseCena)}{r.cenaRucne && <span className="ml-1 text-amber-600" title="ručně přepsáno">✎</span>}
                      </td></tr>
                </tbody>
              </table>

              <div className="mt-3 text-xs text-zinc-500">
                Dodavatel prodává za <strong>{kc(r.dodavatelProdava)}</strong>
                {r.odchylkaProtiDodavateli !== null && (
                  <> — jsme {r.odchylkaProtiDodavateli > 0 ? "+" : ""}
                    {Math.round(r.odchylkaProtiDodavateli * 100)} % proti němu.</>
                )}
              </div>
              {r.odchylkaProtiDodavateli !== null && r.odchylkaProtiDodavateli > 0.2 && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertIcon size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Jsme o hodně výš než dodavatel. Bývá to <strong>chyba v jeho ceníku</strong>,
                    ne v našem vzorci — prodává pod vlastní velkoobchodní cenou. Ověř, než pustíš na eshop.
                  </span>
                </div>
              )}
              <a href={r.velkoobchodUrl} target="_blank" rel="noreferrer"
                 className="mt-3 inline-block text-sm text-ktm-orange hover:underline">
                Otevřít ve velkoobchodu →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Rozměry = varianty */}
      <h2 className="mt-8 mb-2 text-lg font-semibold">Rozměry ({d.rozmery.length})</h2>
      <p className="mb-3 text-sm text-zinc-500">
        Každý rozměr je vlastní varianta se svým skladem a cenou. Klikni na řádek,
        ať se nahoře přepočítá rozpis ceny.
      </p>
      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="w-full min-w-[52rem] text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
              <th className="py-2 px-3 text-left">Rozměr</th>
              <th className="py-2 px-3 text-left">Kód výrobce</th>
              <th className="py-2 px-3 text-right">Nákupka</th>
              <th className="py-2 px-3 text-right">Dodavatel</th>
              <th className="py-2 px-3 text-right">Naše cena</th>
              <th className="py-2 px-3 text-left">Sklad u nich</th>
              <th className="py-2 px-3 text-left">Stav</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {d.rozmery.map((x) => (
              <Radek key={x.azpneuId} x={x} vybrany={x.azpneuId === (r?.azpneuId ?? "")}
                     onVyber={() => setVybrany(x.azpneuId)} onZmena={() => void nacti()} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Popis od dodavatele — plný text */}
      {d.popisHtml && (
        <>
          <h2 className="mt-8 mb-1 text-lg font-semibold">Popis <span className="text-sm font-normal text-zinc-400">od dodavatele</span></h2>
          <p className="mb-3 text-xs text-zinc-400">
            ⚠ Text je společný pro celý dezén, ne pro jednotlivý rozměr. Na eshop
            půjde po úpravě — tohle je originál od zdroje.
          </p>
          <div className="prose prose-sm max-w-3xl text-zinc-700 [&>p]:mb-3"
               dangerouslySetInnerHTML={{ __html: d.popisHtml }} />
        </>
      )}
    </div>
  );
}

function Radek({
  x, vybrany, onVyber, onZmena,
}: {
  x: AzpneuDezenDetail["rozmery"][number];
  vybrany: boolean; onVyber: () => void; onZmena: () => void;
}): JSX.Element {
  const [edituji, setEdituji] = useState(false);
  const [hodnota, setHodnota] = useState(x.naseCena ? String(Math.round(Number(x.naseCena))) : "");
  const [pracuje, setPracuje] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  const beh = async (fn: () => Promise<void>): Promise<void> => {
    setPracuje(true); setChyba(null);
    try { await fn(); onZmena(); }
    catch (e) { setChyba(e instanceof Error ? e.message : "nepovedlo se"); }
    finally { setPracuje(false); }
  };

  return (
    <tr onClick={onVyber}
        className={`border-b border-zinc-50 cursor-pointer ${vybrany ? "bg-orange-50/50" : "hover:bg-zinc-50"}`}>
      <td className="py-2 px-3 font-medium whitespace-nowrap">
        {x.sizeLabel}
        {x.position && <span className="ml-1 text-zinc-400">{x.position === "P" ? "přední" : "zadní"}</span>}
        {x.siIndex && <span className="ml-1 text-zinc-300">{x.siIndex}</span>}
      </td>
      <td className="py-2 px-3 font-mono text-xs text-zinc-400">{x.manufacturerCode ?? "—"}</td>
      <td className="py-2 px-3 text-right tabular-nums">{kc(x.nakupka)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-zinc-500">{kc(x.dodavatelProdava)}</td>
      <td className="py-2 px-3 text-right tabular-nums font-semibold">
        {edituji ? (
          <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input type="number" value={hodnota} onChange={(e) => setHodnota(e.target.value)}
                   className="w-24 rounded border border-zinc-300 px-2 py-1 text-right"
                   placeholder="prázdné = návrh" />
            <Button size="sm" disabled={pracuje} onClick={() => void beh(async () => {
              await nastavCenuAzpneu(x.azpneuId, hodnota.trim() === "" ? null : Number(hodnota));
              setEdituji(false);
            })}>Ulož</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdituji(false)}>×</Button>
          </span>
        ) : (
          <button type="button" className="hover:underline decoration-dotted"
                  onClick={(e) => { e.stopPropagation(); setEdituji(true); }}
                  title={x.cenaRucne ? "Ručně přepsáno" : "Klikni pro ruční cenu"}>
            {kc(x.naseCena)}{x.cenaRucne && <span className="ml-1 text-amber-600">✎</span>}
          </button>
        )}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        {x.sklad.celkem === 0 ? <span className="text-red-600">nemá</span>
          : x.sklad.opava > 0 ? <span className="text-green-700">Opava {x.sklad.opava} ks</span>
          : x.sklad.do24h + x.sklad.do48h > 0 ? <span className="text-green-600">do 48 h ({x.sklad.do24h + x.sklad.do48h})</span>
          : <span className="text-amber-600">2–5 dní ({x.sklad.do2az5d})</span>}
      </td>
      <td className="py-2 px-3">
        {x.stav === "na-eshopu" && <Badge tone="green">Na eshopu</Badge>}
        {x.stav === "ceka" && <Badge tone="blue">Čeká na robota</Badge>}
        {x.stav === "skryto" && <Badge tone="neutral">Skryto</Badge>}
        {x.stav === "chyba" && <Badge tone="red">Chyba</Badge>}
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {x.stav === "na-eshopu" || x.stav === "ceka" ? (
          <Button size="sm" variant="ghost" disabled={pracuje}
                  onClick={() => void beh(() => skrytAzpneu(x.azpneuId))}>Skrýt</Button>
        ) : (
          <Button size="sm" disabled={pracuje || x.sklad.celkem === 0}
                  onClick={() => void beh(() => schvalitAzpneu(x.azpneuId))}>Na eshop</Button>
        )}
        {chyba && <div className="text-xs text-red-600 mt-1 max-w-[14rem] text-right">{chyba}</div>}
      </td>
    </tr>
  );
}
